'use client';

import { useEffect, useState } from 'react';
import { unstable_isUnrecognizedActionError } from 'next/navigation';
import {
  isDeploymentSkew,
  recoverFromDeploymentSkew,
} from '@/lib/deployment-skew';
import { captureRouteError } from '@/lib/telemetry/route-error';

/** All route boundaries share capture, diagnostic ordering and recovery state. */
export function useRouteErrorCapture(
  error: Error & { digest?: string },
  boundary: string
) {
  const deploymentSkew = isDeploymentSkew(error);
  const [state, setState] = useState<{
    error: Error;
    crashEventId: string | null;
    recovering: boolean;
  }>();
  useEffect(() => {
    let active = true;
    void (async () => {
      const staleAction = unstable_isUnrecognizedActionError(error);
      const captured = staleAction ? null : captureRouteError(error, boundary);
      const recovered =
        deploymentSkew &&
        (await recoverFromDeploymentSkew(error, undefined, {
          boundary,
          isActive: () => active,
        }));
      if (!active) {
        return;
      }
      const id = await (captured ??
        (recovered ? null : captureRouteError(error, boundary)));
      if (active) {
        setState({ error, crashEventId: id, recovering: recovered });
      }
    })();
    return () => {
      active = false;
    };
  }, [error, boundary, deploymentSkew]);
  const current = state?.error === error ? state : undefined;
  return {
    deploymentSkew,
    crashEventId: current?.crashEventId ?? null,
    canReport: !deploymentSkew || current?.recovering === false,
  };
}
